/**
 * QuickAddInput - fast-path word entry for the sidebar.
 *
 * Sits above the search input, visually distinct via the + icon.
 * Duplicate check lives in the parent (sidebar has access to all words);
 * this component just handles the input lifecycle and feedback display.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FC, KeyboardEvent } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'

export interface QuickAddResult {
  success: boolean
  reason?: 'duplicate' | 'empty'
}

interface QuickAddInputProps {
  /** Called with the trimmed word value on Enter. Must return success/reason synchronously. */
  onAdd: (word: string) => QuickAddResult
}

const FEEDBACK_DURATION_MS = 2000

export const QuickAddInput: FC<QuickAddInputProps> = ({ onAdd }) => {
  const [value, setValue] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any in-flight timer on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current)
      }
    }
  }, [])

  const showFeedback = useCallback((message: string) => {
    // Cancel previous timer so rapid duplicates don't pile up
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current)
    }

    setFeedback(message)

    feedbackTimerRef.current = setTimeout(() => {
      setFeedback(null)
    }, FEEDBACK_DURATION_MS)
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return

      const trimmed = value.trim()

      if (!trimmed) {
        // Empty input — silently ignore, no feedback needed
        return
      }

      const result = onAdd(trimmed)

      if (result.success) {
        setValue('')
      } else {
        // Don't clear the input — user probably wants to see what they typed
        showFeedback('Already in collection')
      }
    },
    [value, onAdd, showFeedback]
  )

  return (
    <div className="px-4 pt-3 pb-1">
      <label className="sr-only" htmlFor="quick-add-word-input">
        Add a new word to collection
      </label>
      <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-2 text-sm text-gray-700 focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-300 transition">
        <PlusIcon className="h-4 w-4 text-green-500 flex-shrink-0" aria-hidden />
        <input
          id="quick-add-word-input"
          type="text"
          data-testid="quick-add-input"
          className="flex-1 border-none bg-transparent text-sm focus:outline-none placeholder:text-green-400"
          placeholder="Add a word..."
          aria-label="Add a new word to collection"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {feedback && (
        <p
          data-testid="quick-add-feedback"
          className="mt-1 px-3 text-[0.65rem] text-amber-600"
          role="status"
          aria-live="polite"
        >
          {feedback}
        </p>
      )}
    </div>
  )
}

export default QuickAddInput
